import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import { tasks, users, studies, type AssignableUser } from '@/api/endpoints';
import type { Task, TaskInput, TaskType, Priority, TaskStatus, StudySummary } from '@/types';
import { Icon } from './Icon';
import { SearchableSelect } from './SearchableSelect';
import { STUDY_TYPE_GROUPS, STUDY_TYPE_LABELS } from '@/lib/studyTypeGroups';

interface Props {
  open: boolean;
  initial?: Task | null;
  /** Menedżer (OWNER/ADMIN/OFFICE) — może przypisywać innym, edytować i usuwać. */
  canManage: boolean;
  currentUserId: number;
  currentUserName: string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}

export const TYPE_LABELS: Record<TaskType, string> = {
  FIELD_WORK: 'Prace terenowe',
  OFFICE_WORK: 'Prace biurowe',
  ANALYSIS: 'Analiza',
  AI_VERIFICATION: 'Weryfikacja AI',
  VERIFICATION: 'Weryfikacja',
  SIGNATURE: 'Podpis',
  SHIPPING: 'Wysyłka',
  OTHER: 'Inne',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  CRITICAL: 'Krytyczny',
  HIGH: 'Wysoki',
  NORMAL: 'Normalny',
  LOW: 'Niski',
};

export const STATUS_LABELS: Record<TaskStatus, string> = {
  TODO: 'Do zrobienia',
  IN_PROGRESS: 'W realizacji',
  AI_VERIFICATION: 'Weryfikacja AI',
  HUMAN_VERIFICATION: 'Weryfikacja człowieka',
  COMPLETED: 'Zakończone',
};

interface FormState {
  title: string;
  description: string;
  type: TaskType;
  priority: Priority;
  status: TaskStatus;
  deadline: string;
  assigneeId: string;
  studyId: string;
  /** Typ opracowania zadania bez opracowania (kod StudyType); z opracowaniem rozstrzyga jego typ. */
  studyType: string;
}

const EMPTY: FormState = {
  title: '', description: '', type: 'OFFICE_WORK', priority: 'NORMAL',
  status: 'TODO', deadline: '', assigneeId: '', studyId: '', studyType: '',
};

export function TaskFormModal({
  open, initial, canManage, currentUserId, currentUserName, onClose, onSaved, onDeleted,
}: Props) {
  const [data, setData] = useState<FormState>(EMPTY);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [studyList, setStudyList] = useState<StudySummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null); setBusy(false); setDeleting(false);
    if (initial) {
      setData({
        title: initial.title ?? '',
        description: initial.description ?? '',
        type: initial.type,
        priority: initial.priority,
        status: initial.status,
        deadline: initial.deadline ?? '',
        assigneeId: initial.assigneeId != null ? String(initial.assigneeId) : '',
        studyId: initial.studyId != null ? String(initial.studyId) : '',
        studyType: initial.studyType ?? '',
      });
    } else {
      setData({ ...EMPTY, assigneeId: canManage ? '' : String(currentUserId) });
    }
    // listy potrzebne tylko menedżerowi (przypisanie innym) — study picker dla wszystkich
    if (canManage && assignableUsers.length === 0) {
      users.assignable().then(setAssignableUsers).catch(() => { /* niekrytyczne */ });
    }
    if (studyList.length === 0) {
      studies.list().then(setStudyList).catch(() => { /* niekrytyczne */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: data, onClose, open, busy: busy || deleting,
  });

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.title.trim()) { setErr('Tytuł zadania jest wymagany'); return; }
    setBusy(true); setErr(null);
    const payload: TaskInput = {
      title: data.title.trim(),
      description: data.description.trim() || null,
      type: data.type,
      priority: data.priority,
      status: data.status,
      deadline: data.deadline || null,
      studyId: data.studyId ? Number(data.studyId) : null,
      studyType: data.studyId ? null : (data.studyType || null),
      assigneeId: canManage ? (data.assigneeId ? Number(data.assigneeId) : null) : currentUserId,
    };
    const op = initial ? tasks.update(initial.id, payload) : tasks.create(payload);
    op.then(() => { onSaved(); onClose(); })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    if (!initial || !canManage) return;
    if (!window.confirm(`Usunąć zadanie „${initial.title}"? Tej operacji nie można cofnąć.`)) return;
    setDeleting(true); setErr(null);
    tasks.remove(initial.id)
      .then(() => { (onDeleted ?? onSaved)(); onClose(); })
      .catch(e => { setErr(e?.message ?? String(e)); setDeleting(false); });
  };

  const set = <K extends keyof FormState>(k: K) => (v: FormState[K]) => setData(d => ({ ...d, [k]: v }));

  // Z opracowaniem typ jest jego — pole tylko pokazuje, kto zadanie zobaczy do przyjęcia.
  const wybraneOpracowanie = data.studyId ? studyList.find(s => String(s.id) === data.studyId) : undefined;
  const typOpracowania = wybraneOpracowanie ? wybraneOpracowanie.type : data.studyType;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-xl w-full max-w-[640px] max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
          <h2 className="text-[16px] font-semibold text-ink-900">
            {initial ? 'Edytuj zadanie' : 'Nowe zadanie'}
          </h2>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          <label className="field col-span-2">
            <span className="field-label">Tytuł *</span>
            <input value={data.title} onChange={e => set('title')(e.target.value)} className="input"
              placeholder="np. Zebrać dokumenty wejściowe" autoFocus />
          </label>

          <label className="field">
            <span className="field-label">Typ</span>
            <SearchableSelect value={data.type} onChange={v => set('type')(v as TaskType)}>
              {(Object.keys(TYPE_LABELS) as TaskType[]).map(t => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </SearchableSelect>
          </label>

          <label className="field">
            <span className="field-label">Priorytet</span>
            <SearchableSelect value={data.priority} onChange={v => set('priority')(v as Priority)}>
              {(Object.keys(PRIORITY_LABELS) as Priority[]).map(p => (
                <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
              ))}
            </SearchableSelect>
          </label>

          <label className="field">
            <span className="field-label">Termin (opcjonalnie)</span>
            <input type="date" value={data.deadline} onChange={e => set('deadline')(e.target.value)}
              className="input font-mono" />
          </label>

          {initial && (
            <label className="field">
              <span className="field-label">Status</span>
              <SearchableSelect value={data.status} onChange={v => set('status')(v as TaskStatus)}
                disabled={!canManage}>
                {(Object.keys(STATUS_LABELS) as TaskStatus[]).map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </SearchableSelect>
            </label>
          )}

          <label className={'field ' + (initial ? '' : 'col-span-1')}>
            <span className="field-label">Przypisane do</span>
            {canManage ? (
              <SearchableSelect value={data.assigneeId} onChange={set('assigneeId')}>
                <option value="">— nieprzypisane —</option>
                {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
              </SearchableSelect>
            ) : (
              <input value={currentUserName} disabled className="input bg-ink-50 text-ink-500" />
            )}
          </label>

          <label className="field col-span-2">
            <span className="field-label">Opracowanie (opcjonalnie)</span>
            <SearchableSelect value={data.studyId} onChange={set('studyId')}
              placeholder="— bez powiązania —" searchPlaceholder="Szukaj opracowania…">
              <option value="">— bez powiązania —</option>
              {studyList.map(s => <option key={s.id} value={s.id}>{s.number} · {s.title}</option>)}
            </SearchableSelect>
          </label>

          <label className="field col-span-2">
            <span className="field-label">
              Typ opracowania
              <span className="text-ink-400 font-normal">
                {wybraneOpracowanie
                  ? ' · z opracowania'
                  : ' · zadanie bez osoby pokaże się do przyjęcia pracownikom wykonującym ten typ'}
              </span>
            </span>
            <SearchableSelect value={typOpracowania} onChange={set('studyType')}
              disabled={!!wybraneOpracowanie} placeholder="— bez typu —" searchPlaceholder="Szukaj typu…">
              <option value="">— bez typu —</option>
              {STUDY_TYPE_GROUPS.map(g => (
                <optgroup key={g.key} label={g.label}>
                  {g.types.map(t => <option key={t} value={t}>{STUDY_TYPE_LABELS[t]}</option>)}
                </optgroup>
              ))}
            </SearchableSelect>
          </label>

          <label className="field col-span-2">
            <span className="field-label">Opis</span>
            <textarea value={data.description} onChange={e => set('description')(e.target.value)}
              className="textarea" rows={3} placeholder="Szczegóły zadania…" />
          </label>

          {err && <div className="col-span-2 text-red-500 text-[12px]">Błąd: {err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          {initial && canManage && (
            <button type="button" onClick={remove} disabled={deleting || busy}
              className="btn btn-ghost text-red-500 hover:bg-red-50 mr-auto">
              {deleting ? 'Usuwanie…' : 'Usuń zadanie'}
            </button>
          )}
          <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Zapisywanie…' : (initial ? 'Zapisz zmiany' : 'Dodaj zadanie')}
          </button>
        </div>
      </form>
    </div>
  );
}
